import { BehaviorSubject, Subscription } from "rxjs";
import { Order, City, Venue, Timeframe, User } from "../models";
import { ExportToCsv } from "export-to-csv";
import * as moment from "moment";
import { DateService } from "./date.service";
import { EmailRecipient } from "../models/email-recipient";
import { ProductStatus } from "../product/enums/product-status.enum";
import { Product } from "../product/product";
import { ProductType, ScheduleType } from "../product/enums";

export class ProductFilterService {
  public filtered$: BehaviorSubject<Product[]>;
  public filtered: Product[];
  public products: Product[];

  public searchText: string = "";
  public subscription: Subscription;

  public productType: ProductType = null;
  public scheduleType: ScheduleType = null;
  public isActive: boolean = true;
  public isStaged: boolean = true;
  public isExpired: boolean = true;
  public isIncomplete: boolean = true;
  public showHiddenPasses: boolean = true;

  //Paginator
  itemsPerPage: number[] = [10, 20, 50];
  showing: number = 10;
  firstIndex: number = 0;
  lastIndex: number = 0 + this.showing;

  constructor() {
    this.filtered$ = new BehaviorSubject<Product[]>([]);
    this.subscribe();
  }
  next(products: Product[]) {
    this.filtered$.next(products);
  }
  reset() {
    this.searchText = "";
    this.firstIndex = 0;
    this.lastIndex = this.showing;
  }
  unsubscribe() {
    if (this.subscription != null) this.subscription.unsubscribe();
  }

  subscribe() {
    this.subscription = this.filtered$.subscribe(async products => {
      this.products = products;
      if (this.filtered == null || this.filtered.length == 0)
        this.filtered = products;
      this.filter();
    });
  }

  search(text: string) {
    this.searchText = text;
    this.filter();
  }

  filter() {
    this.filtered = this.products.filter(recipient =>
      this.shouldFilter(recipient)
    );
  }

  shouldFilter(product: Product) {
    var productTypeFlag: boolean =
      this.productType == null || product.productType == this.productType;
    var scheduleTypeFlag: boolean =
      this.scheduleType == null || product.scheduleType == this.scheduleType;

    var isActiveFlag: boolean =
      this.isActive && product.status == ProductStatus.Active;
    var isStagedFlag: boolean =
      this.isStaged && product.status == ProductStatus.Staged;
    var isExpiredFlag: boolean =
      this.isExpired && product.status == ProductStatus.Expired;
    var isIncompleteFlag: boolean =
      this.isIncomplete && product.status == ProductStatus.Incomplete;
    var showHiddenPassesFlag =
      this.showHiddenPasses || (!this.showHiddenPasses && product.isActive);

    //If hide passes is false, "Show hidden Passes" is false, So it should filter out
    //Any passes that are not active
    //If hide passes is true, "Show hidden passes" is true, so it should show passes regardless of is_active attribute

    var searchFlag: boolean =
      this.searchText == "" ||
      product.name.toLowerCase().includes(this.searchText.toLowerCase());

    return (
      searchFlag &&
      productTypeFlag &&
      scheduleTypeFlag &&
      (isActiveFlag || isStagedFlag || isExpiredFlag || isIncompleteFlag) &&
      showHiddenPassesFlag
    );
  }

  setItemsPerPage(item: number) {
    this.showing = item;
    this.firstIndex = 0;
    this.lastIndex = item;
  }

  show(i: number) {
    return i >= this.firstIndex && i < this.lastIndex;
  }

  increment() {
    if (this.lastIndex <= this.filtered.length) {
      this.firstIndex += this.showing;
      this.lastIndex += this.showing;
    }
  }
  decrement() {
    if (this.firstIndex - this.showing >= 0) {
      this.firstIndex -= this.showing;
      this.lastIndex -= this.showing;
    }
  }
}

export class OrderFilterService {
  public filtered$: BehaviorSubject<Order[]> = new BehaviorSubject<Order[]>([]);
  public orders: Order[];
  public filtered: Order[];

  public subscription: Subscription;

  public currentCity: City = null;
  public currentVenue: Venue = null;
  public currentType: ProductType = null;
  public showRedeemed: boolean = true;
  public showOutstanding: boolean = true;
  public currentTimeframe: string = "Month to Date";
  public searchText: string = "";
  //Filters
  date: DateService = new DateService();

  public timeframes = {
    Today: this.date.today,
    Yesterday: this.date.yesterday,
    "Week to Date": this.date.weekToDate,
    "Last 7 Days": this.date.last7Days,
    "Last Week": this.date.lastWeek,
    "Last 30 Days": this.date.last30Days,
    "Last Month": this.date.lastMonth,
    "Month to Date": this.date.monthToDate,
    "Year to Date": this.date.yearToDate,
    "Last 12 Months": this.date.last12Months,
    "Last Calendar Year": this.date.lastCalendarYear,
    "All Time": this.date.allTime
  };

  public productTypes = {
    LineSkip: ProductType.LineSkip,
    Drink: ProductType.Drink,
    Cover: ProductType.Cover,
    Other: ProductType.Other
  };

  //Paginator
  itemsPerPage: number[] = [10, 20, 50];
  showing: number = 10;
  firstIndex: number = 0;
  lastIndex: number = 0 + this.showing;

  //Exporting
  csvExporter: ExportToCsv;
  get options() {
    return {
      filename: this.getFileName(),
      fieldSeparator: ",",
      quoteStrings: '"',
      decimalSeparator: ".",
      showLabels: true,
      showTitle: true,
      title: "Orders",
      useTextFile: false,
      useBom: true,
      useKeysAsHeaders: true
    };
  }

  isAdmin: boolean;
  constructor(orders: Order[]) {
    this.subscribe();
    this.update(orders);
  }

  update(orders: Order[]) {
    this.filtered$.next(orders);
  }

  unsubscribe() {
    if (this.subscription != null) this.subscription.unsubscribe();
  }

  subscribe() {
    this.subscription = this.filtered$.subscribe(async orders => {
      this.orders = orders;
      if (this.filtered == null || this.filtered.length == 0)
        this.filtered = orders;
    });
  }

  filter() {
    this.filtered = this.orders.filter(order => this.shouldFilter(order));
  }

  shouldFilter(order: Order) {
    var cityFlag: boolean =
      this.currentCity == null || order.city.name == this.currentCity.city_name;
    var venueFlag: boolean =
      this.currentVenue == null || order.venue.name == this.currentVenue.name;
    var typeFlag: boolean =
      this.currentType == null ||
      order.items[0].product.type == this.currentType;
    var redeemFlag: boolean =
      this.showRedeemed && order.items[0].summary.redeemedAt != null;
    var outstandingFlag: boolean =
      this.showOutstanding && order.items[0].summary.redeemedAt == null;

    var searchFlag: boolean =
      this.searchText == "" ||
      order.user.name.toLowerCase().includes(this.searchText.toLowerCase()) ||
      order.user.email.toLowerCase().includes(this.searchText.toLowerCase());
    return (
      cityFlag &&
      venueFlag &&
      typeFlag &&
      (redeemFlag || outstandingFlag) &&
      searchFlag
    );
  }

  setItemsPerPage(item: number) {
    this.showing = item;
    this.firstIndex = 0;
    this.lastIndex = item;
  }

  show(i: number) {
    return i >= this.firstIndex && i < this.lastIndex;
  }

  increment() {
    if (this.lastIndex <= this.filtered.length) {
      this.firstIndex += this.showing;
      this.lastIndex += this.showing;
    }
  }
  decrement() {
    if (this.firstIndex - this.showing >= 0) {
      this.firstIndex -= this.showing;
      this.lastIndex -= this.showing;
    }
  }

  search(str: string) {
    this.searchText = str;
    this.filter();
    // this.filtered = this.orders.filter(order => {
    //   if (str == "") return true;
    //   return (
    //     order.user.name.toLowerCase().includes(str.toLowerCase()) ||
    //     order.user.email.toLowerCase().includes(str.toLowerCase())
    //   );
    // });
  }

  resetPaginator() {
    this.filtered = [];
    this.firstIndex = 0;
    this.lastIndex = this.showing;
  }

  export() {
    this.csvExporter = new ExportToCsv(this.options);
    let data = this.filtered.map(order => this.toCSV(order));
    this.csvExporter.generateCsv(data);
  }

  toCSV(order: Order) {
    let x = {
      "Purchase Date": moment(order.summary.purchasedAt.toDate()).format("LL"),
      "Purchase Time": moment(order.summary.purchasedAt.toDate()).format("LT"),
      City: order.city.name,
      Venue: order.venue.name,
      "Product Type": ProductType[order.items[0].product.type],
      "Product Name": order.items[0].product.name,
      "Event Date": moment(order.items[0].event.date.toDate()).format("LL"),
      "Customer Name": order.user.name,
      "Customer Email": order.user.email,
      Units: order.items[0].summary.units,
      Price: order.items[0].summary.price,
      Subtotal: order.items[0].summary.subtotal,
      "Service Fee": order.summary.serviceFee,
      Tip: order.summary.tip,
      Total: order.summary.total,
      "Redeem Date":
        order.items[0].summary.redeemedAt != null
          ? moment(order.items[0].summary.redeemedAt.toDate()).format("LL")
          : "N/A",
      "Redeem Time":
        order.items[0].summary.redeemedAt != null
          ? moment(order.items[0].summary.redeemedAt.toDate()).format("LT")
          : "N/A",
      "Additional Product Ingredients": ""
    };
    if (!this.isAdmin) {
      delete x["Customer Email"];
      delete x["Customer Name"];
      delete x["City"];
      x["Customer ID"] = order.user.reference.id;
    }
    return x;
  }

  getFileName(): string {
    let tf: Timeframe = this.timeframes[this.currentTimeframe];
    let begin = moment(tf.begin)
      .format("ll")
      .replace(", ", "")
      .replace(" ", "");
    let end = moment(tf.end)
      .format("ll")
      .replace(", ", "")
      .replace(" ", "");
    return "LineLeapOrders_" + begin + "-to-" + end;
  }
}

export class EmailRecipientFilterService {
  public filtered$: BehaviorSubject<EmailRecipient[]> = new BehaviorSubject<
    EmailRecipient[]
  >([]);

  public recipients: EmailRecipient[];
  public filtered: EmailRecipient[] = [];
  public subscription: Subscription;
  public currentCity: City = null;
  public currentVenue: Venue = null;
  public searchText: string = "";

  itemsPerPage: number[] = [10, 20, 50];
  showing: number = 10;
  firstIndex: number = 0;
  lastIndex: number = 0 + this.showing;
  constructor() {
    this.subscribe();
  }

  next(recipients: EmailRecipient[]) {
    this.filtered$.next(recipients);
  }
  reset() {
    this.currentCity = null;
    this.currentVenue = null;
    this.searchText = "";
    this.firstIndex = 0;
    this.lastIndex = this.showing;
  }
  unsubscribe() {
    if (this.subscription != null) this.subscription.unsubscribe();
  }

  subscribe() {
    this.subscription = this.filtered$.subscribe(async recipients => {
      this.recipients = recipients;
      if (this.filtered == null || this.filtered.length == 0)
        this.filtered = recipients;
      this.filter();
    });
  }

  search(text: string) {
    this.searchText = text;
    this.filter();
  }

  filter() {
    this.filtered = this.recipients.filter(recipient =>
      this.shouldFilter(recipient)
    );
  }

  shouldFilter(recipient: EmailRecipient) {
    var cityFlag: boolean =
      this.currentCity == null ||
      (recipient.cities != null &&
        (recipient.cities
          .map(s => s.split("/")[1])
          .includes(
            this.currentCity.city_name + " " + this.currentCity.state
          ) ||
          recipient.venues
            .map(s => s.split("/")[1])
            .includes(
              this.currentCity.city_name + " " + this.currentCity.state
            )));
    var venueFlag: boolean =
      this.currentVenue == null ||
      (recipient.venues != null &&
        recipient.venues
          .map(s => s.split("/")[s.split("/").length - 1])
          .includes(this.currentVenue.id));

    var searchFlag: boolean =
      this.searchText == "" ||
      recipient.name.toLowerCase().includes(this.searchText.toLowerCase()) ||
      recipient.email.toLowerCase().includes(this.searchText.toLowerCase());

    return cityFlag && venueFlag && searchFlag;
  }

  setItemsPerPage(item: number) {
    this.showing = item;
    this.firstIndex = 0;
    this.lastIndex = item;
  }

  show(i: number) {
    return i >= this.firstIndex && i < this.lastIndex;
  }

  increment() {
    if (this.lastIndex <= this.filtered.length) {
      this.firstIndex += this.showing;
      this.lastIndex += this.showing;
    }
  }
  decrement() {
    if (this.firstIndex - this.showing >= 0) {
      this.firstIndex -= this.showing;
      this.lastIndex -= this.showing;
    }
  }
}

export class UserFilterService {
  public filtered$: BehaviorSubject<User[]> = new BehaviorSubject<User[]>([]);
  public users: User[];
  public filtered: User[] = [];
  public subscription: Subscription;
  public currentCity: City = null;
  public currentVenue: Venue = null;
  public showLineLeap: boolean = true;
  public showVenue: boolean = true;
  public searchText: string = "";
  itemsPerPage: number[] = [10, 20, 50];
  showing: number = 10;
  firstIndex: number = 0;
  lastIndex: number = 0 + this.showing;
  constructor() {}
  init(users: User[]) {
    this.subscribe();
    this.update(users);
  }

  reset() {
    this.currentCity = null;
    this.currentVenue = null;
    this.searchText = "";
    this.showLineLeap = true;
    this.showVenue = true;
    this.firstIndex = 0;
    this.lastIndex = this.showing;
  }

  update(users: User[]) {
    this.filtered$.next(users);
  }

  unsubscribe() {
    if (this.subscription != null) this.subscription.unsubscribe();
  }

  subscribe() {
    this.subscription = this.filtered$.subscribe(async users => {
      this.users = users;
      if (this.filtered == null || this.filtered.length == 0)
        this.filtered = users;
      this.filter();
    });
  }
  search(text: string) {
    this.searchText = text;
    this.filter();
  }

  filter() {
    this.filtered = this.users.filter(user => this.shouldFilter(user));
  }

  shouldFilter(user: User) {
    var cityFlag: boolean =
      this.currentCity == null ||
      user.admin.type.toString() == "LineLeapAdmin" ||
      (user.admin.cities != null &&
        user.admin.cities
          .map(s => s.split("/")[1])
          .includes(this.currentCity.city_name + " " + this.currentCity.state));
    var venueFlag: boolean =
      this.currentVenue == null ||
      user.admin.type.toString() == "LineLeapAdmin" ||
      (user.admin.venues != null &&
        user.admin.venues
          .map(s => s.split("/")[s.split("/").length - 1])
          .includes(this.currentVenue.id)) ||
      cityFlag;

    var searchFlag: boolean =
      this.searchText == "" ||
      user.name.toLowerCase().includes(this.searchText.toLowerCase()) ||
      user.email.toLowerCase().includes(this.searchText.toLowerCase());

    var showVenueFlag: boolean =
      this.showVenue &&
      ["VenueAdmin", "VenueRead"].includes(user.admin.type.toString());
    var showLineLeapFlag: boolean =
      this.showLineLeap &&
      ["LineLeapAdmin", "LineLeapRead"].includes(user.admin.type.toString());
    return (
      cityFlag && venueFlag && searchFlag && (showVenueFlag || showLineLeapFlag)
    );
  }

  setItemsPerPage(item: number) {
    this.showing = item;
    this.firstIndex = 0;
    this.lastIndex = item;
  }

  show(i: number) {
    return i >= this.firstIndex && i < this.lastIndex;
  }

  increment() {
    if (this.lastIndex <= this.filtered.length) {
      this.firstIndex += this.showing;
      this.lastIndex += this.showing;
    }
  }
  decrement() {
    if (this.firstIndex - this.showing >= 0) {
      this.firstIndex -= this.showing;
      this.lastIndex -= this.showing;
    }
  }
}

export class AnalyticsFilterService {
  public filtered$: BehaviorSubject<Order[]> = new BehaviorSubject<Order[]>([]);
  public orders: Order[];
  public filtered: Order[] = [];

  public subscription: Subscription;

  public currentCity: City = null;
  public currentVenue: Venue = null;
  public currentType: ProductType = null;
  public currentTimeframe: string = "Month to Date";
  public currentDow: number[] = null;

  //If filter is on owner side
  public venues: string[];

  date: DateService = new DateService();

  public timeframes = {
    Today: this.date.today,
    Yesterday: this.date.yesterday,
    "Week to Date": this.date.weekToDate,
    "Last 7 Days": this.date.last7Days,
    "Last Week": this.date.lastWeek,
    "Last 30 Days": this.date.last30Days,
    "Last Month": this.date.lastMonth,
    "Month to Date": this.date.monthToDate,
    "Year to Date": this.date.yearToDate,
    "Last 12 Months": this.date.last12Months,
    "Last Calendar Year": this.date.lastCalendarYear,
    "All Time": this.date.allTime
  };

  public productTypes = {
    LineSkip: ProductType.LineSkip,
    Drink: ProductType.Drink,
    Cover: ProductType.Cover,
    Legacy: ProductType.Legacy,
    Other: ProductType.Other
  };

  //Paginator
  itemsPerPage: number[] = [10, 20, 50];
  showing: number = 10;
  firstIndex: number = 0;
  lastIndex: number = 0 + this.showing;

  //Exporting
  csvExporter: ExportToCsv;
  get options() {
    return {
      filename: this.getFileName(),
      fieldSeparator: ",",
      quoteStrings: '"',
      decimalSeparator: ".",
      showLabels: true,
      showTitle: true,
      title: "Orders",
      useTextFile: false,
      useBom: true,
      useKeysAsHeaders: true
    };
  }

  constructor() {}
  init(orders: Order[]) {
    this.subscribe();
    this.update(orders);
  }

  update(orders: Order[]) {
    this.filtered$.next(orders);
  }

  unsubscribe() {
    if (this.subscription != null) this.subscription.unsubscribe();
  }

  subscribe() {
    this.subscription = this.filtered$.subscribe(async orders => {
      this.orders = orders;
      if (this.filtered == null || this.filtered.length == 0)
        this.filtered = orders;
    });
  }

  filter() {
    this.filtered = this.orders.filter(order => this.shouldFilter(order));
  }

  shouldFilter(order: Order) {
    var cityFlag: boolean =
      this.currentCity == null || order.city.name == this.currentCity.city_name;
    var venueFlag: boolean =
      (this.currentVenue == null &&
        (this.venues == null || this.venues.includes(order.venue.name))) ||
      (this.currentVenue != null && order.venue.name == this.currentVenue.name);
    var typeFlag: boolean =
      this.currentType == null ||
      order.items[0].product.type == this.currentType;

    var dowFlag: boolean =
      this.currentDow == null ||
      this.currentDow.includes(order.summary.purchasedAt.toDate().getDay());
    return cityFlag && venueFlag && typeFlag && dowFlag;
  }

  setItemsPerPage(item: number) {
    this.showing = item;
    this.firstIndex = 0;
    this.lastIndex = item;
  }

  show(i: number) {
    return i >= this.firstIndex && i < this.lastIndex;
  }

  increment() {
    if (this.lastIndex <= this.filtered.length) {
      this.firstIndex += this.showing;
      this.lastIndex += this.showing;
    }
  }
  decrement() {
    if (this.firstIndex - this.showing >= 0) {
      this.firstIndex -= this.showing;
      this.lastIndex -= this.showing;
    }
  }

  export() {
    this.csvExporter = new ExportToCsv(this.options);
    let data = this.filtered.map(order => this.toCSV(order));
    this.csvExporter.generateCsv(data);
  }

  toCSV(order: Order) {
    return {
      "Purchase Date": moment(order.summary.purchasedAt.toDate()).format("LL"),
      "Purchase Time": moment(order.summary.purchasedAt.toDate()).format("LT"),
      City: order.city.name,
      Venue: order.venue.name,
      "Product Type": ProductType[order.items[0].product.type],
      "Product Name": order.items[0].product.name,
      "Event Date": moment(order.items[0].event.date.toDate()).format("LL"),
      "Customer Name": order.user.name,
      "Customer Email": order.user.email,
      Units: order.items[0].summary.units,
      Price: order.items[0].summary.price,
      Subtotal: order.items[0].summary.subtotal,
      "Service Fee": order.summary.serviceFee,
      Tip: order.summary.tip,
      Total: order.summary.total,
      "Redeem Date":
        order.items[0].summary.redeemedAt != null
          ? moment(order.items[0].summary.redeemedAt.toDate()).format("LL")
          : "N/A",
      "Redeem Time":
        order.items[0].summary.redeemedAt != null
          ? moment(order.items[0].summary.redeemedAt.toDate()).format("LT")
          : "N/A",
      "Additional Product Ingredients": ""
    };
  }

  getFileName(): string {
    let tf: Timeframe = this.timeframes[this.currentTimeframe];
    let begin = moment(tf.begin)
      .format("ll")
      .replace(", ", "")
      .replace(" ", "");
    let end = moment(tf.end)
      .format("ll")
      .replace(", ", "")
      .replace(" ", "");
    return "LineLeapOrders_" + begin + "-to-" + end;
  }
}
